import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { showToast } from '../lib/toastStore'
import { matchIngredient, getCategoryEmoji, scaleAmount, type RecipePantryItem } from '../lib/recipeUtils'
import { fetchSubstitutes } from '../lib/substituteClient'
import { useRecipeSuggestions } from '../hooks/useRecipeSuggestions'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import { FREE_LIMITS } from '../lib/usageLimits'
import { track } from '../lib/posthog'
import { RecipesMasthead } from '../components/recipes/RecipesMasthead'
import { RecipesSectionHeader } from '../components/recipes/RecipesSectionHeader'
import { CollectionRecipeCard } from '../components/recipes/CollectionRecipeCard'
import { SuggestionCard } from '../components/recipes/SuggestionCard'
import { SavedRecipeModal } from '../components/recipes/SavedRecipeModal'
import RecipeScanner from '../components/pantry/RecipeScanner'
import type { SavedRecipe as RecipeDisplay, SuggestedRecipe } from '../lib/recipeTypes'
import type { SavedRecipeDetail } from '../lib/savedRecipeTypes'
import type { RecipeInstructions } from '../lib/recipeInstructions'
import {
  setIngredientOverride,
  clearIngredientOverride,
  normalizeIngredientName,
} from '../hooks/useIngredientOverride'

type IngredientOverrideRow = {
  ingredient_name: string
  forced_has_it:   boolean
}

/**
 * Resolves whether a recipe ingredient is effectively on hand.
 * Manual overrides win over the auto-match: if the user toggled the
 * have/need circle, that's their answer. Otherwise we fall back to
 * matchIngredient() against the pantry. Used by both the recipeDetail
 * memo (display) and the action handlers (Shop / I made this) so the
 * three surfaces can't drift.
 */
function isIngredientOnHand(
  ingredientName: string,
  pantryItems:    RecipePantryItem[],
  overrideMap:    Map<string, boolean>,
): boolean {
  const key      = normalizeIngredientName(ingredientName)
  const override = overrideMap.get(key)
  if (override !== undefined) return override
  return matchIngredient(ingredientName, pantryItems)
}

// ─── Raw DB types (internal) ──────────────────────────────────────────────────

interface Ingredient {
  name:     string
  amount:   string
  category: string
}

interface RecipeRecord {
  id:             string
  name:           string
  ingredients:    Ingredient[]
  instructions:   RecipeInstructions
  created_at:     string
  base_servings?: number | null
}

interface PantryItem {
  id:   string
  name: string
}

interface Substitute {
  name:  string
  ratio: string
  note:  string
}

interface SubstituteResult {
  role:        string
  substitutes: Substitute[]
}

// ─── SubstituteSheet ──────────────────────────────────────────────────────────

function SubstituteSheet({ ingredient, recipeName, allIngredients, onClose }: {
  ingredient: string; recipeName: string; allIngredients: string[]; onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [result,  setResult]  = useState<SubstituteResult | null>(null)
  const [error,   setError]   = useState('')

  useEffect(() => {
    let cancelled = false
    fetchSubstitutes({ ingredient, recipeName, allIngredients })
      .then(data => { if (!cancelled) setResult(data) })
      .catch(err  => { if (!cancelled) setError(err.message || 'Could not get substitutes') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="fixed inset-0 z-[60] flex flex-col justify-end items-center">
      <motion.div className="absolute inset-0 bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onClose} />
      <motion.div
        className="relative bg-cream rounded-t-[28px] shadow-2xl w-full max-w-[480px] max-h-[75dvh] overflow-y-auto"
        initial={{ y: '100%' }} animate={{ y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="h-1 w-10 rounded-full bg-border-avatar" />
        </div>

        <div className="flex items-start justify-between px-5 pt-2 pb-4 border-b border-border-hairline">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
              Substitute
            </div>
            <h2 className="font-serif text-[20px] font-normal text-ink leading-[1.15] tracking-[-0.015em]">
              Sub for <em className="italic text-forest">{ingredient}</em>
            </h2>
            <p className="font-serif text-[12px] italic text-ink-muted mt-1">
              in {recipeName}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 h-8 w-8 rounded-[10px] border border-border-sand bg-paper flex items-center justify-center text-ink-muted ml-3"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <div className="h-8 w-8 rounded-full border-2 border-forest border-t-transparent animate-spin" />
              <p className="font-serif text-[13px] italic text-ink-muted">Finding substitutes…</p>
            </div>
          ) : error ? (
            <div className="rounded-[12px] bg-critical-bg border border-critical/20 px-4 py-3">
              <p className="font-serif text-[13px] italic text-critical leading-[1.4]">
                {error}
              </p>
            </div>
          ) : result ? (
            <div className="flex flex-col gap-4">
              <div className="rounded-[12px] bg-cream-warm px-4 py-3">
                <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-1">
                  What it does
                </p>
                <p className="font-serif text-[14px] text-ink leading-[1.4]">{result.role}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
                  Substitutes
                </p>
                <div className="flex flex-col gap-3">
                  {result.substitutes.map((sub, i) => (
                    <div key={i} className="rounded-[14px] border border-border-sand bg-paper p-4">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-serif text-[15px] font-medium text-ink tracking-[-0.01em]">
                          {sub.name}
                        </p>
                        <span className="text-[11px] font-semibold text-forest bg-ok-bg px-2 py-0.5 rounded-full">
                          {sub.ratio}
                        </span>
                      </div>
                      <p className="font-serif text-[12px] italic text-ink-muted leading-[1.5]">
                        {sub.note}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </motion.div>
    </div>
  )
}

// ─── Recipes page ─────────────────────────────────────────────────────────────

export default function Recipes() {
  const { user }    = useAuth()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const [showScanner,      setShowScanner]      = useState(false)
  const [selectedRecipe,   setSelectedRecipe]   = useState<RecipeRecord | null>(null)
  const [selectedServings, setSelectedServings] = useState<number>(4)
  const [subIngredient,    setSubIngredient]    = useState<string | null>(null)

  const { suggestions, loading: suggLoading, error: suggError, refresh, expiringCount } =
    useRecipeSuggestions()

  const { data: householdId } = useQuery({
    queryKey: ['householdId', user?.id],
    enabled:  !!user?.id,
    queryFn:  async () => {
      const { data } = await supabase.from('users').select('household_id').eq('id', user!.id).maybeSingle()
      return data?.household_id as string ?? null
    },
  })

  useHouseholdRealtime(householdId, {
    tables: ['saved_recipes', 'recipe_ingredient_overrides'],
    invalidateMap: {
      saved_recipes:               [['saved-recipes', householdId], ['recipe-suggestions']],
      recipe_ingredient_overrides: [['ingredient-overrides'], ['saved-recipes', householdId]],
    },
  })

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ['saved-recipes', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('saved_recipes')
        .select('id, name, ingredients, instructions, created_at, base_servings')
        .eq('household_id', householdId!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return data as RecipeRecord[]
    },
  })

  const { data: pantryItems = [] } = useQuery({
    queryKey: ['pantry-items', householdId],
    enabled:  !!householdId,
    select:   (data: any[]) => data.map((i: any) => ({ id: i.id, name: i.name })) as PantryItem[],
    queryFn:  async () => {
      const { data } = await supabase.from('pantry_items').select('id, name')
        .eq('household_id', householdId!).eq('is_active', true)
      return (data ?? []) as PantryItem[]
    },
  })

  // Per-household manual have/need overrides for the currently-open recipe.
  // When an override exists for an ingredient, it wins over the auto-match.
  const overridesKey = ['ingredient-overrides', selectedRecipe?.id, householdId] as const
  const { data: overrides = [] } = useQuery({
    queryKey: overridesKey,
    enabled:  !!selectedRecipe && !!householdId,
    queryFn:  async (): Promise<IngredientOverrideRow[]> => {
      const { data } = await supabase
        .from('recipe_ingredient_overrides')
        .select('ingredient_name, forced_has_it')
        .eq('household_id', householdId!)
        .eq('recipe_id',    selectedRecipe!.id)
      return (data ?? []) as IngredientOverrideRow[]
    },
  })

  // Reset the servings counter when the user opens a different recipe
  useEffect(() => {
    if (selectedRecipe) setSelectedServings(selectedRecipe.base_servings ?? 4)
  }, [selectedRecipe?.id])  // eslint-disable-line react-hooks/exhaustive-deps

  // Adapt raw RecipeRecord → display SavedRecipe for CollectionRecipeCard
  const displayRecipes = useMemo((): RecipeDisplay[] =>
    recipes.map(r => {
      const onHand = r.ingredients.filter(ing => matchIngredient(ing.name, pantryItems))
      const needed = r.ingredients.filter(ing => !matchIngredient(ing.name, pantryItems))
      return {
        id:                r.id,
        name:              r.name,
        ingredientCount:   r.ingredients.length,
        prepMinutes:       0,
        serves:            r.base_servings ?? 4,
        ingredientsOnHand: onHand.length,
        ingredientsNeeded: needed.map(i => i.name),
      }
    }),
  [recipes, pantryItems])

  // Index overrides by normalized name for O(1) lookup in the adapter
  const overrideMap = useMemo(() => {
    const m = new Map<string, boolean>()
    for (const o of overrides) m.set(o.ingredient_name, o.forced_has_it)
    return m
  }, [overrides])

  // Shape the selected recipe + current servings + pantry state into the type the modal expects.
  // Manual overrides (if present) win over the auto-match from matchIngredient().
  const recipeDetail: SavedRecipeDetail | null = useMemo(() => {
    if (!selectedRecipe) return null
    const baseServings = selectedRecipe.base_servings ?? 4
    return {
      id:           selectedRecipe.id,
      name:         selectedRecipe.name,
      servings:     selectedServings,
      baseServings,
      prepMinutes:  0,
      ingredients:  selectedRecipe.ingredients.map(ing => {
        const scaled    = scaleAmount(ing.amount, baseServings, selectedServings)
        const key       = normalizeIngredientName(ing.name)
        const override  = overrideMap.get(key)
        return {
          id:             ing.name,   // names are unique per recipe — used as the toggle key
          name:           ing.name,
          qtyLabel:       scaled && scaled.trim().length > 0 ? scaled : null,
          hasIt:          isIngredientOnHand(ing.name, pantryItems, overrideMap),
          // manuallyMarked stays gated on the raw override presence so the UI
          // can badge user-touched ingredients distinctly from auto-matched ones.
          manuallyMarked: override !== undefined,
        }
      }),
      instructions: selectedRecipe.instructions ?? null,
    }
  }, [selectedRecipe, selectedServings, pantryItems, overrideMap])

  const handleSuggestionClick = (sug: SuggestedRecipe) => {
    navigate(`/recipe-suggestion/${sug.id}`, { state: { suggestion: sug } })
  }

  // ── Modal handlers (hoisted from the old inline RecipeDetailSheet) ──────────

  async function handleShopForThis() {
    if (!selectedRecipe || !householdId) return
    const baseServings = selectedRecipe.base_servings ?? 4
    const missing = selectedRecipe.ingredients.filter(
      ing => !isIngredientOnHand(ing.name, pantryItems, overrideMap),
    )
    try {
      let { data: activeList } = await supabase.from('shopping_lists').select('id')
        .eq('household_id', householdId).is('completed_at', null)
        .order('generated_at', { ascending: false }).limit(1).maybeSingle()
      if (!activeList) {
        const { data: newList } = await supabase.from('shopping_lists')
          .insert({ household_id: householdId, generated_at: new Date().toISOString() })
          .select('id').single()
        activeList = newList
      }
      if (!activeList) throw new Error('Could not create list')

      for (const ing of missing) {
        const scaled = scaleAmount(ing.amount, baseServings, selectedServings)
        const { data: existing } = await supabase.from('pantry_items').select('id')
          .eq('household_id', householdId).ilike('name', ing.name).limit(1).maybeSingle()
        let itemId = existing?.id
        if (!itemId) {
          const { data: newItem } = await supabase.from('pantry_items').insert({
            household_id:       householdId,
            name:               ing.name.charAt(0).toUpperCase() + ing.name.slice(1),
            emoji:              getCategoryEmoji(ing.category),
            display_category:   ing.category || 'pantry',
            freshness_category: 'pantry_staple',
            is_active:          false,
            confirmation_count: 0,
            location:           'pantry',
            track_expiration:   false,
            status_override:    null,
          }).select('id').single()
          itemId = newItem?.id
        } else {
          await supabase.from('pantry_items').update({ status_override: null }).eq('id', itemId)
        }
        if (!itemId) continue

        const { data: onList } = await supabase.from('list_items').select('id')
          .eq('list_id', activeList.id).eq('item_id', itemId).neq('status', 'removed').maybeSingle()
        if (!onList) {
          await supabase.from('list_items').insert({
            list_id:       activeList.id,
            item_id:       itemId,
            status:        'pending',
            urgency_score: 50,
            sort_order:    0,
            is_one_off:    true,
            notes:         scaled || ing.amount || null,
          })
        }
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      queryClient.invalidateQueries({ queryKey: ['latestList'] })
      queryClient.invalidateQueries({ queryKey: ['listItems'] })
      showToast(`${missing.length} ingredient${missing.length !== 1 ? 's' : ''} added to list 🛒`)
      setSelectedRecipe(null)
    } catch {
      showToast('Something went wrong')
    }
  }

  async function handleMadeThis() {
    if (!selectedRecipe || !householdId) return
    try {
      const now = new Date().toISOString()

      // Resolve each ingredient to a pantry_item id, deduped. If a recipe
      // lists "olive oil" and "extra virgin olive oil" mapping to the same
      // pantry row, we only consume from it once. Skips silently when an
      // override claims "have it" but no pantry row matches.
      const uniqueItems = new Map<string, { id: string; confirmation_count: number }>()
      for (const ing of selectedRecipe.ingredients) {
        if (!isIngredientOnHand(ing.name, pantryItems, overrideMap)) continue

        const { data: pantryItem } = await supabase.from('pantry_items')
          .select('id, confirmation_count').eq('household_id', householdId)
          .ilike('name', ing.name).limit(1).maybeSingle()
        if (!pantryItem) continue
        if (uniqueItems.has(pantryItem.id)) continue
        uniqueItems.set(pantryItem.id, {
          id:                 pantryItem.id,
          confirmation_count: pantryItem.confirmation_count ?? 0,
        })
      }

      // FIFO consume one unit from each unique pantry_item's oldest active
      // batch. Letting the trigger drive current_quantity — no direct write
      // and no status_override='used' (that's reserved for Mark-As-Out).
      let depleted = 0
      for (const item of uniqueItems.values()) {
        const { data: batches } = await supabase
          .from('item_batches')
          .select('id, quantity')
          .eq('item_id', item.id)
          .eq('status', 'active')
          .order('expiry_date', { ascending: true, nullsFirst: false })
          .order('created_at',  { ascending: true })
          .limit(1)

        if (!batches || batches.length === 0) continue
        const batch = batches[0]
        if (batch.quantity <= 1) {
          await supabase.from('item_batches').update({ status: 'used', quantity: 0 }).eq('id', batch.id)
        } else {
          await supabase.from('item_batches').update({ quantity: batch.quantity - 1 }).eq('id', batch.id)
        }

        // last_confirmed_at + confirmation_count are cycle-tracking signals
        // unrelated to quantity — safe to write directly.
        await supabase.from('pantry_items').update({
          last_confirmed_at:  now,
          confirmation_count: item.confirmation_count + 1,
        }).eq('id', item.id)

        depleted++
      }

      queryClient.invalidateQueries({ queryKey: ['pantry-items', householdId] })
      queryClient.invalidateQueries({ queryKey: ['item-batches'] })
      showToast(`${depleted} ingredient${depleted !== 1 ? 's' : ''} marked as used`)
      setSelectedRecipe(null)
    } catch {
      showToast('Something went wrong')
    }
  }

  // Optimistic cache update for the ingredient-overrides query. Returns the
  // previous snapshot so the caller can roll it back if the DB write fails.
  function patchOverridesCache(
    patch: (prev: IngredientOverrideRow[]) => IngredientOverrideRow[],
  ): IngredientOverrideRow[] {
    const prev = (queryClient.getQueryData(overridesKey) as IngredientOverrideRow[] | undefined) ?? []
    queryClient.setQueryData(overridesKey, patch(prev))
    return prev
  }

  async function handleToggleHasIt(ingredientId: string, nextHasIt: boolean) {
    if (!selectedRecipe || !householdId) return
    const name   = ingredientId  // SavedRecipeDetail uses the ingredient name as the id
    const key    = normalizeIngredientName(name)
    const autoHasIt = matchIngredient(name, pantryItems)

    // If the flip matches the auto-match, DROP the override entirely — keeps the
    // table from accumulating redundant rows.
    const shouldClear = nextHasIt === autoHasIt

    const snapshot = patchOverridesCache(prev => {
      const without = prev.filter(o => o.ingredient_name !== key)
      if (shouldClear) return without
      return [...without, { ingredient_name: key, forced_has_it: nextHasIt }]
    })

    const result = shouldClear
      ? await clearIngredientOverride({
          householdId,
          recipeId: selectedRecipe.id,
          ingredientName: name,
        })
      : await setIngredientOverride({
          householdId,
          recipeId: selectedRecipe.id,
          ingredientName: name,
          forcedHasIt: nextHasIt,
        })

    if (!result.ok) {
      // Roll back the cache and surface the real error
      queryClient.setQueryData(overridesKey, snapshot)
      showToast(result.error ?? 'Could not update ingredient')
    }
  }

  async function handleDeleteRecipe() {
    if (!selectedRecipe) return
    // Native confirm preserves the two-step-delete safety from the old inline UI
    if (!window.confirm(`Delete "${selectedRecipe.name}"?`)) return
    const id = selectedRecipe.id
    await supabase.from('saved_recipes').delete().eq('id', id)
    queryClient.invalidateQueries({ queryKey: ['saved-recipes', householdId] })
    showToast('Recipe deleted')
    setSelectedRecipe(null)
  }

  if (isLoading) {
    return (
      <div className="px-4 pt-8 bg-cream min-h-screen">
        <div className="h-[120px] rounded-[18px] bg-[#F0EDE8] animate-pulse mb-5" />
        {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-[18px] bg-[#F0EDE8] animate-pulse mb-[10px]" />)}
      </div>
    )
  }

  return (
    <div className="px-4 pt-8 pb-28 bg-cream min-h-screen">

      <RecipesMasthead
        savedCount={recipes.length}
        suggestionCount={suggestions.length}
        onAddRecipe={() => setShowScanner(true)}
      />

      {/* ── Collection ── */}
      <section className="mb-7">
        <RecipesSectionHeader
          title={<>Your <em className="italic">collection</em></>}
          countLabel={`${recipes.length} saved`}
        />

        {recipes.length === 0 ? (
          <div className="rounded-[18px] border border-dashed border-border-sand bg-paper/60 px-4 py-8 text-center">
            <p className="font-serif text-[15px] italic text-ink-muted">
              Nothing saved yet. Add your first recipe above.
            </p>
          </div>
        ) : (
          displayRecipes.map(display => (
            <CollectionRecipeCard
              key={display.id}
              recipe={display}
              onClick={id => setSelectedRecipe(recipes.find(r => r.id === id) ?? null)}
            />
          ))
        )}
      </section>

      {/* ── Cook before they go ── */}
      {expiringCount > 0 && (
        <section>
          <RecipesSectionHeader
            title={<>Cook before they <em className="italic text-terracotta">go</em></>}
            badge={{ text: `${expiringCount} soon`, tone: 'soon' }}
          />

          <p className="font-serif text-[13px] italic text-ink-muted mb-3 leading-[1.45] px-0.5">
            AI suggestions using what's ripening in your fridge.
          </p>

          {suggLoading && (
            <div className="rounded-[18px] border border-border-sand bg-paper px-4 py-6 text-center">
              <p className="font-serif text-[13px] italic text-ink-muted">
                Thinking through tonight's options…
              </p>
            </div>
          )}

          {suggError === 'limit_reached' && !suggLoading && (
            <div className="rounded-[18px] border border-border-sand bg-paper px-5 py-6 text-center">
              <p className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
                Limit reached
              </p>
              <p className="font-serif text-[16px] text-ink leading-[1.2] mb-2">
                Out of <em className="italic text-forest">AI suggestions</em>
              </p>
              <p className="text-xs text-ink-muted leading-[1.5] mb-4 max-w-[280px] mx-auto">
                Free accounts get {FREE_LIMITS.ai_suggestions} AI suggestions per month. Resets on the 1st.
              </p>
              <button
                type="button"
                onClick={() => navigate('/paywall')}
                className="rounded-2xl bg-forest text-forest-ink px-5 py-2.5 text-sm font-semibold"
              >
                Upgrade for unlimited
              </button>
            </div>
          )}

          {suggError && suggError !== 'limit_reached' && !suggLoading && (
            <div className="rounded-[18px] border border-border-sand bg-paper px-4 py-5 flex items-center justify-between gap-3">
              <p className="font-serif text-[13px] italic text-ink-muted">
                Couldn't reach suggestions right now.
              </p>
              <button
                onClick={refresh}
                className="rounded-[10px] bg-forest px-3 py-[7px] text-[11px] font-semibold text-forest-ink"
              >
                Retry
              </button>
            </div>
          )}

          {!suggLoading && !suggError && suggestions.map((s, idx) => (
            <SuggestionCard
              key={s.id}
              suggestion={s}
              variant={idx === 0 ? 'featured' : 'compact'}
              onClick={handleSuggestionClick}
            />
          ))}
        </section>
      )}

      {/* ── RecipeScanner ── */}
      {showScanner && householdId && (
        <RecipeScanner
          householdId={householdId}
          pantryItems={pantryItems}
          onClose={() => setShowScanner(false)}
          onSuccess={showToast}
          onSave={async (name, ingredients, instructions) => {
            const { error } = await supabase.from('saved_recipes').insert({
              household_id:  householdId,
              name,
              ingredients,
              instructions,
              base_servings: 4,
              created_at:    new Date().toISOString(),
            })
            if (error) { console.error('Save recipe error:', error); showToast('Could not save recipe'); return }
            queryClient.invalidateQueries({ queryKey: ['saved-recipes', householdId] })
            track('recipe_added', { source: 'scan' })
            showToast(`"${name}" saved to your recipes 📖`)
            setShowScanner(false)
          }}
        />
      )}

      {/* ── Saved-recipe modal (new editorial design) ── */}
      {recipeDetail && selectedRecipe && (
        <SavedRecipeModal
          recipe={recipeDetail}
          onClose={() => setSelectedRecipe(null)}
          onServingsChange={setSelectedServings}
          onShopForThis={handleShopForThis}
          onIMadeThis={handleMadeThis}
          onDelete={handleDeleteRecipe}
          onSubstitute={id => setSubIngredient(id)}
          onToggleHasIt={handleToggleHasIt}
        />
      )}

      {/* ── Substitute sheet (stacks above the saved-recipe modal) ── */}
      <AnimatePresence>
        {subIngredient && selectedRecipe && (
          <SubstituteSheet
            ingredient={subIngredient}
            recipeName={selectedRecipe.name}
            allIngredients={selectedRecipe.ingredients.map(i => i.name)}
            onClose={() => setSubIngredient(null)}
          />
        )}
      </AnimatePresence>

    </div>
  )
}
